import './style.scss';

import { NotificationInstance } from 'antd/es/notification/interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { ApiOrders } from 'api';
import { metricsBaseUrl } from 'api/apiBase/baseUrl';
import { CexOrder } from 'entities/cex-order';
import { useTypedDispatch, useTypedSelector } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { ICexAccount } from 'types/accounts';
import { ICexActiveOrderBot } from 'types/cex/orders';
import { IChartOrder } from 'types/charts';

interface IGetOrdersArgs {
  selectedAccounts: ICexAccount[];
  accountsToAPI: (selectedAccounts: ICexAccount[]) => {
    array: number[];
    optional?: number[];
  };
}

interface IInnerActiveOrder extends CexOrder {
  label: string;
}

const botToOrder = (bot: ICexActiveOrderBot) => {
  switch (bot) {
    case 'abuse_protection': {
      return 'AP';
    }
    case 'arbitrage': {
      return 'AR';
    }
    case 'buysell': {
      return 'BS';
    }
    case 'cm': {
      return 'CM';
    }
    case 'counter-order': {
      return 'CO';
    }
    case 'fg': {
      return 'FG';
    }
    case 'order-manager-bot': {
      return 'OM';
    }
    case 'orderbook': {
      return 'OB';
    }
    case 'orderbook_dynamic': {
      return 'OBD';
    }
    case 'ob_protection': {
      return 'OBP';
    }
    case 'organic': {
      return 'ORG';
    }
    case 'regular': {
      return 'REG';
    }
    case 'unknown': {
      return 'UNK';
    }
    default: {
      return '';
    }
  }
};

const useActiveOrders = ({
  accounts,
  pairId,
  notificationApi,
}: {
  accounts: ICexAccount[];
  pairId: number;
  notificationApi: NotificationInstance;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [activeOrders, setActiveOrders] = useState<IInnerActiveOrder[]>([]);
  const [activeOrdersCount, setActiveOrdersCount] = useState<number>(0);
  const [cancelingOrderLoading, setCancelingOrderLoading] = useState<string | undefined>(undefined);

  const placedOrderIds = useRef<string[]>([]);

  const dispatch = useTypedDispatch();
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);
  const jwt = useTypedSelector(store => store.auth.jwt);

  const formatOrders = useCallback(
    (orders: CexOrder[] = [], accounts: ICexAccount[]) =>
      orders
        .map(el => {
          const findAccount = accounts.find(account => account.id === el.account_id);

          return {
            ...el,
            account_notes: findAccount ? findAccount.notes : undefined,
            label: botToOrder(el.bot),
          };
        })
        .sort((a, b) => {
          const aPrice = Number(a.price);
          const bPrice = Number(b.price);

          if (aPrice >= bPrice) return -1;

          if (bPrice >= aPrice) return 1;

          return 0;
        }),
    [],
  );

  const getOrders = useCallback(
    async ({ selectedAccounts, accountsToAPI }: IGetOrdersArgs, options?: { silent: boolean }) => {
      const isSilent = options?.silent;

      try {
        if (!isAdmin) return;

        if (!isSilent) setLoading(true);

        const { data } = await ApiOrders.getOrders({
          pair_id: pairId,
          limit: 100,
          account_ids: accountsToAPI(selectedAccounts).array,
          statuses: ['active'],
        });

        setActiveOrders(formatOrders(data?.orders ?? [], accounts));
        setActiveOrdersCount(Number(data?.total_count?.toString() ?? 0));
      } catch (error) {
        console.log('error: ', error);
      } finally {
        if (!isSilent) {
          setLoading(false);
        }
      }
    },
    [pairId, formatOrders, isAdmin, accounts],
  );

  useEffect(() => {
    if (isAdmin) {
      const ws = new ReconnectingWebSocket(
        metricsBaseUrl + `/api/v1/orders/ws/active?pair_id=${pairId}`,
        jwt ? ['Bearer', jwt] : [],
        {
          maxRetries: 10,
          minReconnectionDelay: 1000,
        },
      );

      ws.onmessage = (event: any) => {
        try {
          const newOrders = JSON.parse(event.data);

          setActiveOrders(formatOrders(newOrders, accounts));
          setActiveOrdersCount(newOrders?.length ?? 0);
        } catch (error) {
          console.log('error: ', error);
        }
      };

      return () => {
        ws.close();
      };
    }
    //eslint-disable-next-line
  }, [jwt, pairId, accounts]);

  useEffect(() => {
    if (isAdmin) {
      const ws = new ReconnectingWebSocket(
        metricsBaseUrl + `/api/v1/orders/ws/placement-status?pair_id=${pairId}`,
        jwt ? ['Bearer', jwt] : [],
        {
          maxRetries: 10,
          minReconnectionDelay: 1000,
        },
      );

      ws.onmessage = (event: any) => {
        try {
          const data = JSON.parse(event.data) as {
            id: string;
            status: 'success' | 'failed';
            error?: string;
          };

          if (placedOrderIds.current.includes(data.id)) {
            const { id, status, error } = data;

            placedOrderIds.current = [...placedOrderIds.current].filter(el => el !== id);

            if (status === 'success') {
              notificationApi.success({
                title: 'Order placed successfully',
                description: `Order ID: ${id}`,
                className: 'cex-order-placement-success-notification',
              });
            } else {
              notificationApi.error({
                title: 'Order placement failed',
                description: error,
                className: 'cex-order-placement-failed-notification',
              });
            }
          }
        } catch (error) {
          console.log('error: ', error);
        }
      };

      return () => {
        ws.close();
      };
    }

    //eslint-disable-next-line
  }, [jwt, pairId]);

  const chartOrders = useMemo<IChartOrder[]>(
    () =>
      activeOrders.map(order => ({
        side: order.side,
        price: Number(order.price),
        amount: order.base_amount,
        label: order.label,
        account_id: order.account_id,
        pair_id: order.pair_id,
        cex_order_id: order.cex_order_id,
      })),
    [activeOrders],
  );

  const onCancelOrders = useCallback(
    async (orders: { account_id: number; cex_order_id: string }[]) => {
      try {
        dispatch(dropAlertState());

        const result = await ApiOrders.cancelOrders({
          account_id: orders[0].account_id,
          pair_id: pairId,
          orders: orders.map(order => order.cex_order_id),
        });

        if (!result.isSuccess) {
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: result.errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
        }

        setCancelingOrderLoading(undefined);

        return { isSuccess: result.isSuccess };
      } catch (error) {
        console.log('error: ', error);
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
        return { isSuccess: false };
      }
    },
    [pairId, dispatch],
  );

  const onCancelByAccount = useCallback(
    async (accountId: number) => {
      try {
        const { isSuccess, errorMessage } = await ApiOrders.cancelOrdersByAccount({
          account_id: accountId,
          pair_id: pairId,
        });

        if (!isSuccess) {
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
        }

        return { isSuccess };
      } catch (error) {
        console.log('error: ', error);
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );

        return { isSuccess: false };
      }
    },
    [pairId, dispatch],
  );

  const setPlacedOrderId = useCallback((id: string) => {
    placedOrderIds.current.push(id);
  }, []);

  return {
    getOrders,
    loading,
    activeOrders,
    activeOrdersCount,
    chartOrders,
    cancelingOrderLoading,
    onCancelOrders,
    onCancelByAccount,
    setPlacedOrderId,
  };
};

export { useActiveOrders };
